import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMemberData } from '../../contexts/MemberDataContext';
import { MemberSortKey } from '../../types/memberData';
import { MemberRatingSummary } from '../../types/memberReview';
import { getMemberRatingSummary } from '../../services/memberReviewService';
import { formatDate } from '../../utils/dateUtils';
import './MemberCard.css';

const MemberList: React.FC = () => {
  const navigate = useNavigate();
  const {
    state,
    displayedMembers,
    filteredCount,
    loadMembers,
    loadMoreMembers,
    setFilters,
    setSort,
    clearFilters,
  } = useMemberData();

  const { membersLoading, membersError, pagination, filters, sort } = state;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Rating summaries keyed by member ID — fetched lazily as members appear
  const [ratingSummaries, setRatingSummaries] = useState<Map<number, MemberRatingSummary>>(new Map());
  const fetchingIds = useRef<Set<number>>(new Set());

  // Load members on mount if not loaded
  useEffect(() => {
    if (!state.membersLoaded && !membersLoading) {
      loadMembers();
    }
  }, [state.membersLoaded, membersLoading, loadMembers]);

  // Fetch rating summaries for newly visible members (lazy, non-blocking)
  useEffect(() => {
    const missing = displayedMembers.filter(
      m => !ratingSummaries.has(m.id) && !fetchingIds.current.has(m.id)
    );
    if (missing.length === 0) return;

    missing.forEach(m => fetchingIds.current.add(m.id));

    Promise.allSettled(missing.map(m => getMemberRatingSummary(m.id))).then(results => {
      setRatingSummaries(prev => {
        const next = new Map(prev);
        results.forEach((result, i) => {
          if (result.status === 'fulfilled') {
            next.set(missing[i].id, result.value);
          }
          fetchingIds.current.delete(missing[i].id);
        });
        return next;
      });
    });
  }, [displayedMembers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll observer
  const setupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && pagination.hasMore && !membersLoading) {
          loadMoreMembers();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }
  }, [pagination.hasMore, membersLoading, loadMoreMembers]);

  useEffect(() => {
    setupObserver();
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [setupObserver]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ search: e.target.value });
  };

  const handleClearSearch = () => {
    clearFilters();
  };

  const handleMemberClick = (memberId: number) => {
    navigate(`/members/${memberId}`);
  };

  const handleSort = (key: MemberSortKey) => {
    setSort(key);
  };

  const getSortIndicator = (key: MemberSortKey) => {
    if (sort.key !== key) return null;
    return sort.direction === 'asc' ? ' ▲' : ' ▼';
  };

  if (membersLoading && displayedMembers.length === 0) {
    return <div className="no-results"><p>Loading members...</p></div>;
  }

  if (membersError && displayedMembers.length === 0) {
    return (
      <div className="no-results">
        <p>{membersError}</p>
        <button onClick={() => loadMembers()} className="btn btn-secondary">Retry</button>
      </div>
    );
  }

  return (
    <>
      <div className="member-list-header">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={filters.search}
            onChange={handleSearchChange}
            className="search-input"
          />
          {filters.search && (
            <button
              className="search-clear"
              onClick={handleClearSearch}
              aria-label="Clear search"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      <div className="member-list">
        {displayedMembers.length === 0 ? (
          <div className="no-results">
            <p>No members found{filters.search ? ` matching "${filters.search}"` : ''}</p>
          </div>
        ) : (
          <table className="member-table">
            <thead>
              <tr>
                <th className="sortable-header" onClick={() => handleSort('name')}>
                  Name{getSortIndicator('name')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('username')}>
                  Username{getSortIndicator('username')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('email')}>
                  Email{getSortIndicator('email')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('phone')}>
                  Phone{getSortIndicator('phone')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('createdAt')}>
                  Joined{getSortIndicator('createdAt')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('lastLoginDate')}>
                  Last Login{getSortIndicator('lastLoginDate')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('status')}>
                  Status{getSortIndicator('status')}
                </th>
                <th>Rating</th>
              </tr>
            </thead>
            <tbody>
              {displayedMembers.map(member => (
                <tr
                  key={member.id}
                  onClick={() => handleMemberClick(member.id)}
                  className="member-row"
                >
                  <td className="member-name">
                    {member.firstName} {member.lastName}
                  </td>
                  <td>{member.username}</td>
                  <td>{member.email}</td>
                  <td>{member.phone || '-'}</td>
                  <td>{member.createdAt ? formatDate(member.createdAt) : '-'}</td>
                  <td>{member.lastLoginDate ? formatDate(member.lastLoginDate) : 'Never'}</td>
                  <td>
                    <span className={`status-badge status-badge--${member.banned ? 'inactive' : 'active'}`}>
                      {member.banned ? 'Banned' : 'Active'}
                    </span>
                  </td>
                  <td>
                    {(() => {
                      const summary = ratingSummaries.get(member.id);
                      if (!summary) return <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>—</span>;
                      if (summary.reviewCount === 0) return <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>—</span>;
                      if (summary.flaggedCount > 0) {
                        return (
                          <span className="member-rating-badge member-rating-badge--flagged">
                            &#9888; {summary.avgRating.toFixed(1)} &#9733;
                          </span>
                        );
                      }
                      return (
                        <span className="member-rating-badge member-rating-badge--stars">
                          {summary.avgRating.toFixed(1)} &#9733;
                        </span>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Infinite scroll trigger */}
        <div ref={loadMoreRef} style={{ height: '20px', margin: '10px 0' }}>
          {membersLoading && displayedMembers.length > 0 && (
            <p style={{ textAlign: 'center', color: '#666' }}>Loading more...</p>
          )}
        </div>
      </div>

      <div className="member-count">
        Showing {displayedMembers.length} of {filteredCount} members
        {filteredCount !== pagination.totalCount && ` (${pagination.totalCount} total)`}
      </div>
    </>
  );
};

export default MemberList;
